import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Icon, Text } from '@factorialco/deprecated-design-system'

import MultipleChoiceWithState from 'modules/core/components/Filter/filters/MultipleChoice'
import SingleChoiceWithState from 'modules/core/components/Filter/filters/SingleChoice'
import Popover from 'modules/core/components/PopoverMenu'
import Selector from 'modules/core/components/Selector'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'

import { useIncidenceStats } from 'modules/bookkeepers/hooks/IncidenceStatsContext'
import type { IFilters } from 'modules/bookkeepers/hooks/useIncidenceFilters'
import { useIncidenceFilterContext } from 'modules/bookkeepers/lib/IncidenceFilterContext'
import { useMessageFilterContext } from 'modules/bookkeepers/lib/MessageFilterContext'
import { trackIncidenceFilter } from 'modules/bookkeepers/lib/amplitudeData'
import { GroupedChoice } from 'modules/payroll_bundle/components/GroupedChoice'
import MonthPickerFilter from 'modules/payroll_bundle/components/MonthPickerFilter'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import { getIncidenceStatsCount } from 'modules/payroll_bundle/lib/Incidences/IncidenceFilters'
import { incidenceTypeAsGroupedSelectOptions as incidenceTypeOptions } from 'modules/payroll_bundle/lib/Incidences/IncidenceFilters'
import { LEAVE_CHANGE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'

import { legalEntitiesOptions, statusOptions } from './filterOptions'

interface Props {
  messageTab: boolean
}

const DATE_FILTER_TYPE = 'date'
const IS_BOOKKEEPERVIEW = false

const IncidencesFilters = ({ messageTab }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const incidenceStats = useIncidenceStats()
  const incidenceFilters = useIncidenceFilterContext()
  const messageFilters = useMessageFilterContext()
  const hasDevCompensations = hasFeature('dev_compensations')
  const incidencesRoute = hasDevCompensations
    ? navigation.compensations.employeeUpdates.fullPath()
    : navigation.payrollBundle.incidences.fullPath()

  useAsyncData(() => Promise.all([leaveTypes.fetch()]), [])

  const typeLeavesFiltersList = [
    LEAVE_CHANGE_TYPES.sick,
    LEAVE_CHANGE_TYPES.parental,
    LEAVE_CHANGE_TYPES.custom_leave,
  ]

  const leaveTypesOptions = leaveTypes.active
    .filter((leaveType) => !typeLeavesFiltersList.includes(leaveType.attributes.name))
    .map((leaveType) => {
      return {
        value:
          leaveType.attributes.identifier === 'custom'
            ? leaveType.name
            : leaveType.attributes.identifier,
        label: leaveType.name,
      }
    })

  const { filters, filterDate, onFilterChange, onDateChange } = messageTab
    ? messageFilters
    : incidenceFilters

  const incidenceCounts = getIncidenceStatsCount(incidenceStats, filters.status).get()

  const handleFilterChange = (key: keyof IFilters, value: string | string[]) => {
    onFilterChange(key, value)
    const trackValue = Array.isArray(value) ? value.join(',') : value
    trackFilter(key, trackValue)

    navigate(incidencesRoute)
  }

  const handleDateChange = (value?: string) => {
    onDateChange(value || '')

    trackFilter(DATE_FILTER_TYPE, value || '')
    navigate(incidencesRoute)
  }

  const { status, legal_entity_id, name, custom_leave_name } = filters

  const parseFilterChange = (value: undefined | string | string[]): string | string[] => {
    if (Array.isArray(value)) {
      //Removes falsy values from the array
      return value.filter(Boolean)
    }
    return value || ''
  }

  const parseGroupedChoiceValue = (value: undefined | string): string[] => {
    if (!value) return []
    return value.split(',').filter(Boolean)
  }

  const { hasInhousePayroll } = useInhousePayroll()

  const trackFilter = (filterType: keyof IFilters | string, value: string) => {
    trackIncidenceFilter(filterType, value, IS_BOOKKEEPERVIEW, hasInhousePayroll)
  }

  const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)

  function handlePopoverToggle(newPopoverState: boolean) {
    setIsPopoverOpen(newPopoverState)
  }

  const [hover, setHover] = React.useState<boolean>(false)

  const handleTypeValueChange = (action: string[]) => {
    const stringAction = action?.join(',') || ''
    const isCustomLeave = stringAction === LEAVE_CHANGE_TYPES.custom_leave
    const hasFilterMatch =
      action?.some((actionItem) => typeLeavesFiltersList.includes(actionItem)) ?? false

    if (isCustomLeave) {
      handlePopoverToggle(true)
    } else {
      // update the filters and close the popover
      handleFilterChange('name', stringAction)
      handlePopoverToggle(false)

      if (hasFilterMatch) {
        // Deselect custom leave selector if the action matches the filters list
        handleFilterChange('custom_leave_name', [])
      }
    }
  }

  const handleCustomLeaveChange = (value: unknown) => {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    const stringValueArray = value as string[]

    const isTypeLeaveActive = parseGroupedChoiceValue(name).some((item) =>
      typeLeavesFiltersList.includes(item)
    )

    // Deselect type selector if any of these values is checked
    if (stringValueArray.length > 0 && isTypeLeaveActive) {
      handleFilterChange('name', [])
    }

    handleFilterChange('custom_leave_name', stringValueArray)
  }

  const [isTypeFilterOpen, setIsTypeFilterOpen] = React.useState(false)

  const handleTypeFilterVisibility = (newValue: boolean) => {
    setIsTypeFilterOpen((_prev) => newValue)
  }

  const handleCustomLeaveBackTransition = () => {
    handlePopoverToggle(false)
    handleTypeFilterVisibility(true)
  }

  return (
    <Box gap="s8" flexDirection="row" width="full" justifyContent="center" alignItems="flexEnd">
      <MonthPickerFilter
        label={i18n.t('bookkeepers.incidence.filters.date')}
        value={filterDate.value}
        onValueChange={(value?: string) => handleDateChange(value)}
      />
      <Box position="relative">
        <GroupedChoice
          label={i18n.t('bookkeepers.incidence.filters.type')}
          value={parseGroupedChoiceValue(name)}
          noFooter
          onValueChange={handleTypeValueChange}
          options={incidenceTypeOptions(incidenceCounts, custom_leave_name)}
          isOpen={isTypeFilterOpen}
          handleFilterVisibility={handleTypeFilterVisibility}
        />
        <Popover type="list" open={isPopoverOpen} onClick={handlePopoverToggle}>
          <Popover.Trigger></Popover.Trigger>
          <Popover.Content side="bottom" align="center" width="s360">
            <Box paddingX="s6">
              <Box
                justifyContent="flexStart"
                alignItems="center"
                flexDirection="row"
                gap="s8"
                paddingY="s8"
              >
                <Box
                  onClick={handleCustomLeaveBackTransition}
                  cursor="pointer"
                  aria-label="go-back"
                >
                  <Icon.Medium icon="chevron-left" />
                </Box>

                <Text.Normal size="300" weight="normal" color="grey1000">
                  {i18n.t('bookkeepers.incidence.filters.types.custom_leave_2')}
                </Text.Normal>
              </Box>
              <Selector.Container
                searchPlaceholder={i18n.t('common.search')}
                blankLabel={i18n.t('common.empty')}
                value={custom_leave_name?.split(',') || []}
                isMultiSelect
                onChange={handleCustomLeaveChange}
              >
                {leaveTypesOptions.map((option) => (
                  <Selector.Option
                    key={option.value}
                    value={option.value}
                    label={option.label}
                    searchValue={option.label}
                  >
                    {option.label}
                  </Selector.Option>
                ))}
              </Selector.Container>
              <Box paddingTop="s8">
                <Box
                  onClick={() => {
                    handleFilterChange('custom_leave_name', [])
                    handlePopoverToggle(false)
                    setHover(false)
                  }}
                  width="full"
                  onMouseEnter={() => setHover(true)}
                  onMouseLeave={() => setHover(false)}
                  background={hover ? 'radicalRed050' : 'transparent'}
                  justifyContent="center"
                  alignItems="center"
                  height="s48"
                  borderRadius={{ all: 'abs008' }}
                >
                  <Text.Normal size="300" weight="medium" color="radicalRed1200">
                    {i18n.t('filters.clear_filter')}
                  </Text.Normal>
                </Box>
              </Box>
            </Box>
          </Popover.Content>
        </Popover>
      </Box>
      <SingleChoiceWithState
        label={i18n.t('bookkeepers.incidence.filters.status')}
        options={statusOptions(IS_BOOKKEEPERVIEW, hasInhousePayroll)}
        value={parseFilterChange(status)}
        onValueChange={(action) => handleFilterChange('status', action)}
      />
      <MultipleChoiceWithState
        label={i18n.t('bookkeepers.incidence.filters.legal_entity')}
        options={legalEntitiesOptions()}
        value={
          Array.isArray(legal_entity_id) ? legal_entity_id : (legal_entity_id || '').split(',')
        }
        onValueChange={(action) => handleFilterChange('legal_entity_id', action || [])}
      />
    </Box>
  )
}

export default IncidencesFilters
